<script>
  import { FirebaseApp, User, Doc } from "sveltefire";
  import { createEventDispatcher } from "svelte";
  import { lookupByEmail } from "../directory.js";
  import { getDisplayNames } from "../profiles.js";
  import { ensureInviteCode, inviteLink, inviteMessage } from "../invites.js";

  export let firebase;
  export let prefs = {};
  export let auth = null;

  const dispatch = createEventDispatcher();

  // Friends the signed-in user has added: [{ id, email, name }]
  let friends = [];
  let loadedIds = null;      // ids currently reflected in `friends`
  let dirty = false;         // unsaved changes
  let busy = false;
  let emailInput = "";
  let errorMessage = "";
  let infoMessage = "";
  let inviteEmail = "";     // set when a lookup finds no account, so we can offer an invite

  // Invite link: one code per account (src/invites.js). Whoever sets up PassCall
  // through it lands on this list, and this account on theirs, with nothing to type.
  let inviteCode = null;
  let inviteBusy = false;
  let inviteError = "";
  let copied = false;
  let invitePrepared = false;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const isHandheld = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  $: link = inviteCode ? inviteLink(inviteCode) : "";

  async function prepareInvite(user, fresh = false) {
    if (inviteBusy) return;
    inviteBusy = true;
    inviteError = "";
    copied = false;
    try {
      inviteCode = await ensureInviteCode(firebase.firestore(), firebase, user, prefs && prefs.name, { fresh });
    } catch (e) {
      console.warn("invite link not ready", e);
      inviteError = "Your invite link isn't available right now. Please try again in a moment.";
    } finally {
      inviteBusy = false;
    }
  }

  function messageFor(user) {
    return inviteMessage(inviteCode, (prefs && prefs.name) || (user && user.displayName) || "");
  }
  function mailto(user, toEmail = "") {
    return `mailto:${encodeURIComponent(toEmail)}?subject=${encodeURIComponent("Join me on PassCall")}&body=${encodeURIComponent(messageFor(user))}`;
  }
  function smsHref(user) {
    // "sms:?&body=" is read by both iOS and Android.
    return `sms:?&body=${encodeURIComponent(messageFor(user))}`;
  }
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      copied = true;
      setTimeout(() => { copied = false; }, 2500);
    } catch (e) {
      window.prompt("Copy your invite link:", link);
    }
  }
  async function share(user) {
    try {
      await navigator.share({ title: "Join me on PassCall", text: messageFor(user), url: link });
    } catch (e) {
      /* the person closed the share sheet */
    }
  }

  function goHome() {
    dispatch("modeChange", { action: "welcomeWindow" });
  }
  function goOnline() {
    dispatch("modeChange", { action: "window" });
  }

  function sameIds(a, b) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  // Friends paused with "Not today": kept in Friendships/{uid}.paused, never matched.
  let paused = [];
  let pausedLoaded = false;

  // Load the user's own friends only: display names from Profiles (one get per friend;
  // no listing, and no access to anyone's email or settings).
  async function loadFriends(data) {
    const ids = data && Array.isArray(data.friends) ? data.friends.filter((id) => typeof id === "string" && id) : [];
    if (dirty || (loadedIds && sameIds(loadedIds, ids))) return;
    loadedIds = ids;
    const db = firebase.firestore();
    const current = firebase.auth().currentUser;
    if (!invitePrepared && current) {
      invitePrepared = true;
      prepareInvite(current);
    }
    if (!pausedLoaded && current) {
      pausedLoaded = true;
      try {
        const doc = await db.collection("Friendships").doc(current.uid).get();
        const d = doc.exists ? doc.data() : null;
        paused = d && Array.isArray(d.paused) ? d.paused : [];
      } catch (e) {
        paused = [];
      }
    }
    const names = await getDisplayNames(db, ids);
    friends = ids.map((id) => ({ id, name: names[id] || "" }));
  }

  function togglePause(id) {
    paused = paused.includes(id) ? paused.filter((p) => p !== id) : [...paused, id];
    dirty = true;
    infoMessage = "";
  }

  function findUserByEmail(email) {
    return lookupByEmail(firebase.firestore(), email);
  }

  async function addFriend(user) {
    errorMessage = "";
    infoMessage = "";
    inviteEmail = "";
    const email = emailInput.trim();
    if (!email) { errorMessage = "Enter the email address your friend signed up with."; return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { errorMessage = "That doesn't look like an email address."; return; }
    if (user && user.email && email.toLowerCase() === user.email.toLowerCase()) { errorMessage = "That's you. Add someone else."; return; }
    busy = true;
    try {
      const found = await findUserByEmail(email);
      if (!found) { errorMessage = "No PassCall account uses that email yet."; inviteEmail = email; return; }
      if (found.id === (user && user.uid)) { errorMessage = "That's you. Add someone else."; return; }
      if (friends.some((f) => f.id === found.id)) { errorMessage = `${found.email} is already on your list.`; return; }
      friends = [...friends, found];
      dirty = true;
      emailInput = "";
    } catch (e) {
      console.warn("friend lookup failed", e);
      errorMessage = "Couldn't look that up right now. Please try again.";
    } finally {
      busy = false;
    }
  }

  function removeFriend(id) {
    friends = friends.filter((f) => f.id !== id);
    dirty = true;
    errorMessage = "";
    infoMessage = "";
  }

  async function save(docRef) {
    errorMessage = "";
    infoMessage = "";
    busy = true;
    const ids = friends.map((f) => f.id);
    try {
      await docRef.update({ friends: ids });
      const pausedIds = paused.filter((id) => ids.includes(id));
      await firebase.firestore().collection("Friendships").doc(docRef.id).set({ approved: ids, paused: pausedIds }, { merge: true });
      paused = pausedIds;
      loadedIds = ids;
      dirty = false;
      infoMessage = ids.length ? "Friends saved." : "Friends saved. Your list is empty, so no calls will connect until you add someone.";
    } catch (e) {
      console.warn("saving friends failed", e);
      errorMessage = "Couldn't save your friends. Please try again.";
    } finally {
      busy = false;
    }
  }
</script>

<FirebaseApp {firebase}>
  <User let:user>
    <Doc path={`Users/${user.uid}`} on:data={(e) => loadFriends(e.detail.data)} let:ref={docRef}>
      <div class="friends">
        <header class="friends__top">
          <div class="friends__bar">
            <a class="friends__brand" href="https://passcall.io/" aria-label="PassCall site">
              <img src="/images/PC-website-hd-logo.png" alt="PassCall" width="299" height="108" />
            </a>
            <nav class="friends__nav" aria-label="Account">
              <button type="button" on:click={goHome}>Home</button>
              <button type="button" on:click={goOnline}>Open the window</button>
            </nav>
          </div>
        </header>

        <main class="friends__main">
          <p class="friends__eyebrow">Your circle</p>
          <h1 class="friends__title">The people you'd <em>stop and chat</em> with.</h1>
          <p class="friends__lede">
            Invite someone with your link, or add a friend by the email address they registered with. Only people on this list can see you when the window is open.
          </p>

          <section class="friends__card friends__card--invite" aria-labelledby="invite-title">
            <h2 id="invite-title" class="friends__card-title">Invite someone</h2>
            <p class="friends__text">Send your link. Whoever sets up PassCall with it is on your list at once, and you're on theirs. Nothing to type on either side.</p>
            {#if inviteCode}
              <div class="friends__linkbox">
                <code class="friends__link">{link}</code>
                <button type="button" class="friends__btn friends__btn--small" on:click={copyLink}>{copied ? "Copied" : "Copy link"}</button>
              </div>
              <div class="friends__row friends__row--invite">
                <a class="friends__btn friends__btn--ghost" href={mailto(user)}>Email</a>
                {#if isHandheld}<a class="friends__btn friends__btn--ghost" href={smsHref(user)}>Text message</a>{/if}
                {#if canShare}<button type="button" class="friends__btn friends__btn--ghost" on:click={() => share(user)}>Share…</button>{/if}
              </div>
              <p class="friends__hint friends__hint--invite">Anyone who has this link can add you. <button type="button" class="friends__textbtn" on:click={() => prepareInvite(user, true)} disabled={inviteBusy}>Get a new link</button> if it reached someone it shouldn't have.</p>
            {:else if inviteError}
              <p class="friends__note friends__note--error" role="alert">{inviteError} <button type="button" class="friends__textbtn" on:click={() => prepareInvite(user)}>Try again</button></p>
            {:else}
              <p class="friends__hint">Preparing your link…</p>
            {/if}
          </section>

          <section class="friends__card" aria-labelledby="add-title">
            <h2 id="add-title" class="friends__card-title">Add a friend</h2>
            <form class="friends__add" on:submit|preventDefault={() => addFriend(user)}>
              <label class="friends__label" for="friend-email">Friend's email</label>
              <div class="friends__row">
                <input
                  id="friend-email"
                  type="email"
                  bind:value={emailInput}
                  placeholder="name@example.com"
                  autocomplete="off"
                  autocapitalize="none"
                  spellcheck="false"
                  disabled={busy}
                />
                <button class="friends__btn" type="submit" disabled={busy}>{busy ? "Looking up…" : "Add"}</button>
              </div>
            </form>
            {#if errorMessage}
              <p class="friends__note friends__note--error" role="alert">
                {errorMessage}
                {#if inviteEmail}
                  {#if inviteCode}<a class="friends__invite" href={mailto(user, inviteEmail)}>Email {inviteEmail} your invite link</a>{/if}
                {/if}
              </p>
            {/if}
            {#if infoMessage}
              <p class="friends__note friends__note--ok" role="status">{infoMessage}</p>
            {/if}

            <h2 class="friends__card-title friends__card-title--list">Your friends</h2>
            {#if friends.length === 0}
              <p class="friends__empty">Nobody yet. Add the first person above.</p>
            {:else}
              <ul class="friends__list">
                {#each friends as friend (friend.id)}
                  <li class:friends__row--paused={paused.includes(friend.id)}>
                    <div class="friends__who">
                      <span class="friends__name">{friend.name || "Friend"}</span>
                      {#if paused.includes(friend.id)}<span class="friends__email">Paused. Windows won't open with them until you resume.</span>{/if}
                    </div>
                    <div class="friends__row-actions">
                      <button type="button" class="friends__remove" on:click={() => togglePause(friend.id)} disabled={busy} aria-label={`${paused.includes(friend.id) ? "Resume" : "Pause"} ${friend.name || "friend"}`}>
                        {paused.includes(friend.id) ? "Resume" : "Not today"}
                      </button>
                      <button type="button" class="friends__remove" on:click={() => removeFriend(friend.id)} disabled={busy} aria-label={`Remove ${friend.name || "friend"}`}>Remove</button>
                    </div>
                  </li>
                {/each}
              </ul>
            {/if}

            <div class="friends__actions">
              <button class="friends__btn friends__btn--save" type="button" on:click={() => save(docRef)} disabled={busy || !dirty}>
                {dirty ? "Save changes" : "Saved"}
              </button>
              {#if dirty}<span class="friends__hint">Unsaved changes</span>{/if}
            </div>
          </section>
        </main>

        <footer class="friends__foot">
          <span>PassCall</span>
          <a href="mailto:help@passcall.io">help@passcall.io</a>
        </footer>
      </div>
    </Doc>
  </User>
</FirebaseApp>

<style>
  .friends { min-height: 100vh; display: flex; flex-direction: column; font-family: var(--font); color: var(--on-dark);
    background: radial-gradient(70% 55% at 10% 0%, rgba(31, 107, 63, .58), transparent 65%), radial-gradient(45% 45% at 92% 70%, rgba(212, 175, 55, .2), transparent 65%), var(--ink); }
  .friends__top { background: var(--wood); border-bottom: 2px solid var(--wood-trim); box-shadow: 0 6px 18px rgba(0, 0, 0, .35); }
  .friends__bar { max-width: 1200px; margin: 0 auto; padding: 0 16px; min-height: 68px; display: flex; align-items: center; gap: 1.5rem; }
  .friends__brand img { height: 34px; width: auto; filter: brightness(0) invert(1); display: block; }
  .friends__nav { margin-left: auto; display: flex; gap: .5rem; flex-wrap: wrap; }
  .friends__nav button { font: inherit; font-weight: 500; font-size: .95rem; padding: .5rem .9rem; border-radius: 999px; border: 1.5px solid rgba(244, 237, 227, .35); background: transparent; color: var(--on-dark); cursor: pointer; }
  .friends__nav button:hover { background: var(--on-dark); color: var(--ink); }

  .friends__main { flex: 1; max-width: 1200px; width: 100%; margin: 0 auto; padding: clamp(2.5rem, 7vw, 5rem) 16px; }
  .friends__eyebrow { margin: 0 0 1rem; font-size: .78rem; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--amber); display: inline-flex; align-items: center; gap: .5rem; }
  .friends__eyebrow::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
  .friends__title { font-family: var(--display); font-weight: 500; font-size: clamp(2.2rem, 4vw + .5rem, 3.6rem); line-height: 1.05; letter-spacing: -.025em; margin: 0 0 1rem; max-width: 18ch; }
  .friends__title em { background: var(--gold-metal); -webkit-background-clip: text; background-clip: text; color: transparent; font-style: italic; }
  .friends__lede { margin: 0 0 2rem; max-width: 52ch; font-size: 1.1rem; line-height: 1.5; color: var(--on-dark-muted); }

  .friends__card { max-width: 620px; padding: 1.75rem; border-radius: 18px; background: var(--paper); color: var(--ink); box-shadow: 0 24px 60px rgba(0, 0, 0, .35); }
  .friends__card-title { font-family: var(--display); font-weight: 500; font-size: 1.5rem; margin: 0 0 1rem; }
  .friends__card-title--list { margin-top: 2rem; }
  .friends__label { display: block; font-size: .9rem; font-weight: 600; margin-bottom: .4rem; }
  .friends__row { display: flex; gap: .5rem; flex-wrap: wrap; }
  .friends__row input { flex: 1 1 220px; font: inherit; font-size: 1rem; padding: .8rem 1rem; border-radius: 12px; border: 1.5px solid var(--sand); background: #fff; color: var(--ink); }
  .friends__row input:focus { outline: 2px solid var(--red); outline-offset: 1px; border-color: var(--red); }
  .friends__btn { font: inherit; font-weight: 600; font-size: 1rem; padding: .8rem 1.4rem; border: 0; border-radius: 999px; background: var(--gold-metal); color: var(--ink); cursor: pointer; transition: filter .15s; }
  .friends__btn:hover:not(:disabled) { filter: brightness(1.06); }
  .friends__btn:disabled { opacity: .55; cursor: default; }
  .friends__btn--ghost { background: transparent; color: var(--ink); border: 1.5px solid var(--sand); text-decoration: none; display: inline-block; }
  .friends__btn--small { padding: .55rem 1rem; font-size: .9rem; white-space: nowrap; }
  .friends__card--invite { margin-bottom: 1.25rem; }
  .friends__text { margin: 0 0 1rem; color: #4f4741; line-height: 1.5; }
  .friends__linkbox { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; padding: .6rem .6rem .6rem .9rem; border-radius: 12px; background: var(--cream); border: 1.5px solid var(--sand); margin-bottom: .75rem; }
  .friends__link { flex: 1 1 200px; font-size: .95rem; overflow-wrap: anywhere; color: var(--ink); }
  .friends__row--invite { margin-bottom: .75rem; }
  .friends__hint--invite { margin: 0; }
  .friends__textbtn { font: inherit; font-size: inherit; color: var(--ink); background: none; border: 0; padding: 0; text-decoration: underline; cursor: pointer; }
  .friends__textbtn:disabled { opacity: .5; cursor: default; }
  .friends__btn--ghost:hover:not(:disabled) { background: var(--cream); border-color: var(--ink); }
  .friends__invite { display: inline-block; margin-top: .5rem; text-decoration: none; font: inherit; font-weight: 600; font-size: .9rem; padding: .45rem .9rem; border-radius: 999px; border: 0; background: var(--gold-metal); color: var(--ink); cursor: pointer; }
  .friends__invite:hover { filter: brightness(1.06); }
  .friends__btn--save { background: var(--ink); color: var(--on-dark); }
  .friends__btn--save:hover:not(:disabled) { background: var(--ink-3); }
  .friends__note { margin: .85rem 0 0; padding: .7rem .9rem; border-radius: 10px; font-size: .95rem; }
  .friends__note--error { background: rgba(158, 42, 43, .1); color: var(--danger); }
  .friends__note--ok { background: rgba(91, 208, 138, .18); color: #1f6b3f; }
  .friends__empty { margin: 0; color: #6b625b; }
  .friends__list { list-style: none; margin: 0; padding: 0; border-top: 1px solid var(--sand); }
  .friends__row-actions { display: flex; gap: .5rem; flex-wrap: wrap; justify-content: flex-end; }
  .friends__row--paused .friends__name { opacity: .6; }
  .friends__list li { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: .8rem 0; border-bottom: 1px solid var(--sand); }
  .friends__who { display: flex; flex-direction: column; min-width: 0; }
  .friends__name { font-weight: 600; overflow-wrap: anywhere; }
  .friends__email { font-size: .9rem; color: #6b625b; overflow-wrap: anywhere; }
  .friends__remove { font: inherit; font-size: .9rem; padding: .4rem .8rem; border-radius: 999px; border: 1.5px solid var(--sand); background: transparent; color: var(--ink); cursor: pointer; flex-shrink: 0; }
  .friends__remove:hover:not(:disabled) { border-color: var(--red); color: var(--red); }
  .friends__actions { margin-top: 1.5rem; display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
  .friends__hint { font-size: .9rem; color: #6b625b; }

  .friends__foot { border-top: 1px solid var(--line-dark); color: var(--on-dark-muted); font-size: .875rem; padding: 1.25rem 16px; display: flex; flex-wrap: wrap; justify-content: center; gap: .5rem 2rem; }
  .friends__foot a { color: var(--on-dark); text-decoration: none; }
</style>
